import React from "react";
import { FaTimes } from "react-icons/fa";

import { OTHER_OPTION_ID } from "../../lib/api";
import type {
  SurveyAnswer,
  SurveyQuestion,
  SurveyResponseApi,
} from "../../lib/api";
import {
  formatDuration,
  formatOrdinalDate,
  placementLabel,
  respondentName,
} from "./surveyTypes";

type Props = {
  open: boolean;
  response: SurveyResponseApi | null;
  questions: SurveyQuestion[];
  onClose: () => void;
};

/** Renders one answer in the shape its question type implies. */
function answerText(
  answer: SurveyAnswer,
  question: SurveyQuestion | undefined
): string {
  // "Other" arrives as the OTHER_OPTION_ID sentinel in `value`, with the typed text
  // alongside in `otherText` — it is a selected option like any other, so it resolves to a
  // label here rather than short-circuiting the whole answer.
  const labelFor = (id: string) => {
    if (id === OTHER_OPTION_ID) {
      return answer.otherText ? `Other: ${answer.otherText}` : "Other";
    }
    return question?.options?.find((o) => o.id === id)?.label ?? id;
  };

  if (Array.isArray(answer.value)) {
    return answer.value.length === 0
      ? "—"
      : answer.value.map(labelFor).join(", ");
  }
  if (typeof answer.value === "number") {
    const max = question?.scale?.max;
    return max ? `${answer.value} / ${max}` : String(answer.value);
  }
  if (typeof answer.value === "string" && answer.value.trim()) {
    return question &&
      (question.type === "SINGLE_CHOICE" || question.type === "MULTI_CHOICE")
      ? labelFor(answer.value)
      : answer.value;
  }
  // A text question can be skipped yet still carry "Other"-style free text.
  if (answer.otherText) return `Other: ${answer.otherText}`;
  return "—";
}

const ResponseDetailModal: React.FC<Props> = ({
  open,
  response,
  questions,
  onClose,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !response) return null;

  const meta = [
    {
      label: "Status",
      value: response.status === "COMPLETED" ? "Completed" : "Partial",
    },
    {
      label: "Came from",
      value: response.placement ? placementLabel(response.placement) : "—",
    },
    { label: "Started", value: formatOrdinalDate(response.startedAt) },
    { label: "Time taken", value: formatDuration(response.durationSeconds) },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Survey response"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl border border-[#2C363E] bg-[#161E25]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[#2C363E] px-5 py-4">
          <div className="min-w-0">
            <h2 className="font-sora text-[16px] font-semibold text-white">
              {respondentName(response.user)}
            </h2>
            {response.user?.email && (
              <p className="mt-0.5 truncate text-[12px] text-[#869AA9]">
                {response.user.email}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-lg border border-[#3A4852] bg-[#1C2227] p-2 text-[#AEBCC6] transition hover:text-white"
          >
            <FaTimes className="text-sm" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <dl className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {meta.map((m) => (
              <div key={m.label}>
                <dt className="text-[11px] text-[#5C6B76]">{m.label}</dt>
                <dd className="mt-0.5 text-[13px] font-medium text-[#E7EBEE]">
                  {m.value}
                </dd>
              </div>
            ))}
          </dl>

          <ol className="space-y-3">
            {questions.map((q, i) => {
              const answer = response.answers.find(
                (a) => a.questionKey === q.key
              );
              return (
                <li
                  key={q.key}
                  className="rounded-xl border border-[#2C363E] bg-[#13181B] px-4 py-3"
                >
                  <p className="text-[11px] text-[#5C6B76]">Question {i + 1}</p>
                  <p className="mt-0.5 text-[13px] font-medium leading-snug text-[#C6D0D7]">
                    {q.prompt}
                  </p>
                  <p
                    className={`mt-2 text-[14px] leading-relaxed ${
                      answer ? "text-white" : "italic text-[#5C6B76]"
                    }`}
                  >
                    {answer ? answerText(answer, q) : "Not answered"}
                  </p>
                  {answer?.usedSuggestions &&
                    answer.usedSuggestions.length > 0 && (
                      <p className="mt-1.5 text-[11px] text-[#869AA9]">
                        Started from: {answer.usedSuggestions.join(", ")}
                      </p>
                    )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ResponseDetailModal;
